import { authState } from '../../lib/auth.js';
import { getBackendUrl } from '../../lib/backend.js';
import { BaseComponent } from '../../lib/base-component.js';
import {
    authorizeRequestBody,
    CLIENT_CONSENT_DENIED,
    CLIENT_CONSENT_GRANTED,
    clientConsentView,
} from '../../lib/oauth-consent.js';
import {
    clearPendingOAuth,
    readPendingOAuth,
    savePendingOAuthFromAuthorizeUrl,
} from '../../lib/pending-oauth.js';
import { css, html } from '../../vendor/lit.min.js';
import '../login-form.js';

class OAuthAuthorizeView extends BaseComponent {
    static properties = {
        error: { type: String, state: true },
        loading: { type: Boolean, state: true },
        // The API's consent_required answer for a self-registered client, shown
        // until the user approves or denies it.
        consentPrompt: { type: Object, state: true },
    };

    static styles = css`
        :host { display: block; }
        .login-card { max-width: 420px; margin: 0 auto; }
        .consent-host { word-break: break-all; }
    `;

    constructor() {
        super();
        this.error = '';
        this.loading = false;
        this.consentPrompt = null;
    }

    async connectedCallback() {
        super.connectedCallback();

        // Stash the request so it survives whatever the sign-in does to the URL —
        // an Entra round trip, the /setup screen for a new account, a magic link.
        // Each of those exits resumes from here (see lib/pending-oauth.js).
        savePendingOAuthFromAuthorizeUrl(
            window.location.pathname + window.location.search,
            sessionStorage,
        );

        // If already authenticated, skip to generating the auth code
        if (authState.isAuthenticated) {
            this._generateAuthCode();
        } else {
            // Wait for auth init to complete — isAuthenticated may not be set yet.
            // <login-form> calls authState.notify() on a successful local sign-in,
            // and the Entra redirect resolves auth during init; either fires this.
            this._unsubscribeAuth = authState.subscribe((state) => {
                if (!state.loading && state.isAuthenticated && !this._authCodeGenerated) {
                    this._authCodeGenerated = true;
                    this._generateAuthCode();
                }
            });
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (this._unsubscribeAuth) this._unsubscribeAuth();
    }

    _getOAuthParams() {
        return (
            readPendingOAuth() || {
                clientId: '',
                redirectUri: '',
                responseType: 'code',
                scope: '',
                state: '',
                audience: '',
                resource: [],
                nonce: '',
                codeChallenge: '',
                codeChallengeMethod: '',
                tenantSlug: '',
            }
        );
    }

    /**
     * Ask the API for a code. A self-registered client is answered first with a
     * consent prompt; the user's choice comes back through here as `consent`.
     * @param {string} [consent] CLIENT_CONSENT_GRANTED or CLIENT_CONSENT_DENIED
     */
    async _generateAuthCode(consent) {
        this.loading = true;
        const params = this._getOAuthParams();

        try {
            const token = authState.getAccessToken();
            const response = await fetch(`${getBackendUrl()}/api/v1/authorize`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(authorizeRequestBody(params, consent)),
            });

            const data = await response.json();
            if (response.status === 401) {
                // Token is stale/invalid - clear it and show login
                authState.clearTokens();
                authState.isAuthenticated = false;
                authState.user = null;
                this._authCodeGenerated = false;
                this.loading = false;
                return;
            }
            if (!response.ok) {
                this.error = data.message || 'Authorization failed';
                this.loading = false;
                return;
            }

            if (data.consent_required) {
                this.consentPrompt = data;
                this.loading = false;
                return;
            }

            // Clean up and redirect back to the client app -- with a code, or
            // with an error the client has to hear about (a denial, a resource
            // it can't have).
            clearPendingOAuth();
            window.location.href = data.redirect_url;
        } catch (err) {
            this.error = err.message;
            this.loading = false;
        }
    }

    _renderConsent() {
        const view = clientConsentView(this.consentPrompt);

        return html`
            <div class="login-card mt-5">
                <div class="card shadow">
                    <div class="card-body p-4">
                        <h4 class="card-title mb-3">
                            <span class="consent-host">${view.clientHost}</span> wants to access ${view.apiName} as you
                        </h4>
                        ${
                            view.showClientName
                                ? html`<p class="text-muted mb-3">It calls itself <strong>${view.clientName}</strong>.</p>`
                                : ''
                        }
                        ${
                            view.scopes.length
                                ? html`
                            <p class="mb-1">It is asking for:</p>
                            <ul class="mb-3">${view.scopes.map((scope) => html`<li><code>${scope}</code></li>`)}</ul>
                        `
                                : ''
                        }
                        <p class="mb-3">
                            <small class="text-muted">
                                Approving sends you to <strong class="consent-host">${view.redirectHost}</strong>.
                            </small>
                        </p>
                        ${
                            view.loopbackOnly
                                ? html`
                            <div class="alert alert-warning d-flex align-items-start gap-2 py-2">
                                <i class="bi bi-exclamation-triangle-fill" style="margin-top: 0.1rem;"></i>
                                <small>This app runs on your own computer. Approve it only if you just started it, since any program there could claim to be it.</small>
                            </div>
                        `
                                : ''
                        }
                        ${this.error ? html`<div class="alert alert-danger py-2"><small>${this.error}</small></div>` : ''}
                        <div class="d-flex justify-content-end gap-2">
                            <button class="btn btn-outline-secondary"
                                ?disabled="${this.loading}"
                                @click="${() => this._answerConsent(CLIENT_CONSENT_DENIED)}">Deny</button>
                            <button class="btn btn-primary"
                                ?disabled="${this.loading}"
                                @click="${() => this._answerConsent(CLIENT_CONSENT_GRANTED)}">Approve</button>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    _answerConsent(consent) {
        this.error = '';
        this._generateAuthCode(consent);
    }

    render() {
        if (this.consentPrompt) {
            return this._renderConsent();
        }

        if (this.loading && !this.error) {
            return html`
                <div class="login-card mt-5">
                    <div class="card shadow">
                        <div class="card-body p-4 text-center">
                            <div class="spinner-border text-primary" role="status"></div>
                            <p class="mt-3 text-muted">Authorizing...</p>
                        </div>
                    </div>
                </div>
            `;
        }

        return html`
            <div class="login-card mt-5">
                <div class="card shadow">
                    <div class="card-body p-4">
                        <h3 class="card-title text-center mb-4">Sign In</h3>

                        ${this.error ? html`<div class="alert alert-danger">${this.error}</div>` : ''}

                        <login-form></login-form>
                    </div>
                </div>
                <div class="text-center mt-3">
                    <small class="text-muted">
                        <a href="/terms" class="text-decoration-none">Terms of Service</a>
                        <span class="mx-2">|</span>
                        <a href="/privacy" class="text-decoration-none">Privacy Policy</a>
                    </small>
                </div>
            </div>
        `;
    }
}

customElements.define('oauth-authorize-view', OAuthAuthorizeView);
export { OAuthAuthorizeView };
